import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Spinner, useToast } from '@centraoneplus/ui';
import { ticketService, type MyTicketAccessState } from '@services/ticket.service';

const REASON_MAX = 500;

function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const message = e?.response?.data?.message;
  return (Array.isArray(message) ? message.join(', ') : message) || e?.message || fallback;
}

function when(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '';
}

interface TicketAccessRequestPanelProps {
  ticketId: string;
  /** The caller can open the ticket now (e.g. approved meanwhile): reload it. */
  onAccessGranted: () => void;
}

/**
 * "No access to this ticket" → ask for it. Only an agent @mentioned on the
 * ticket may ask; admins, owners and the ticket's team supervisor are notified
 * and decide. Shows where the caller's request stands: open (with withdraw),
 * declined (with the approver's note), or why they cannot ask.
 *
 * Renders nothing if the state cannot be loaded — the page's own "no access"
 * message still stands.
 */
export function TicketAccessRequestPanel({ ticketId, onAccessGranted }: TicketAccessRequestPanelProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['ticket-access-request', 'mine', ticketId];
  const { data: state, isLoading } = useQuery<MyTicketAccessState>({
    queryKey,
    queryFn: () => ticketService.getMyAccessRequest(ticketId),
    retry: false,
  });
  const [reason, setReason] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey });

  if (isLoading) {
    return (
      <div className="mb-7 flex justify-center" aria-label="Loading access request">
        <Spinner size="sm" />
      </div>
    );
  }
  if (!state) return null;

  if (state.hasAccess) {
    return (
      <div className="mb-7 rounded-[2px] border border-[var(--co-line-strong)] p-4 text-left text-[13.5px]" data-testid="access-granted">
        <p className="mb-3 text-[var(--co-ink-2)]">You have access to this ticket now.</p>
        <Button size="sm" onClick={onAccessGranted}>Open ticket</Button>
      </div>
    );
  }

  const handleRequest = async () => {
    setIsBusy(true);
    try {
      await ticketService.requestAccess(ticketId, reason.trim() || undefined);
      toast.success('Request sent', 'An admin or your supervisor will be notified.');
      setReason('');
    } catch (err) {
      toast.error('Could not send the request', apiErrorMessage(err, 'Please try again.'));
    } finally {
      setIsBusy(false);
      refresh();
    }
  };

  const handleWithdraw = async () => {
    if (!state.request) return;
    setIsBusy(true);
    try {
      await ticketService.cancelAccessRequest(ticketId, state.request.id);
      toast.success('Request withdrawn');
    } catch (err) {
      toast.error('Could not withdraw the request', apiErrorMessage(err, 'Please try again.'));
    } finally {
      setIsBusy(false);
      refresh();
    }
  };

  const box = 'mb-7 rounded-[2px] border border-[var(--co-line-strong)] p-4 text-left text-[13.5px] leading-[1.55]';

  if (state.canRequest) {
    return (
      <div className={box} data-testid="access-request-form">
        <p className="mb-3 text-[var(--co-ink-2)]">
          You were mentioned on this ticket. Ask for access and an admin or the team's supervisor will review it.
        </p>
        <label htmlFor="access-reason" className="mb-1 block text-[12px] font-medium text-[var(--co-ink-3)]">
          Why do you need it? (optional)
        </label>
        <textarea
          id="access-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
          rows={3}
          maxLength={REASON_MAX}
          disabled={isBusy}
          placeholder="e.g. I was asked to check the refund amount"
          className="w-full rounded-[2px] border border-[var(--co-line-strong)] bg-transparent px-3 py-2 text-[13px] text-[var(--co-ink)] focus:border-[var(--co-brass)] focus:outline-none"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-[11.5px] text-[var(--co-ink-4)]">{reason.length}/{REASON_MAX}</span>
          <Button size="sm" onClick={handleRequest} disabled={isBusy}>
            {isBusy ? <Spinner size="sm" /> : 'Request access'}
          </Button>
        </div>
      </div>
    );
  }

  const request = state.request;
  switch (state.blockedReason) {
    case 'pending':
      return (
        <div className={box} data-testid="access-request-pending">
          <p className="mb-1 font-medium text-[var(--co-ink)]">Access requested</p>
          <p className="mb-3 text-[var(--co-ink-3)]">
            Sent {when(request?.createdAt)}. You'll get a notification when an admin or the team's supervisor answers.
          </p>
          <Button size="sm" variant="outline" onClick={handleWithdraw} disabled={isBusy}>
            {isBusy ? <Spinner size="sm" /> : 'Withdraw request'}
          </Button>
        </div>
      );
    case 'rejected':
      return (
        <div className={box} data-testid="access-request-rejected">
          <p className="mb-1 font-medium text-[var(--co-ink)]">
            Request declined{request?.decidedByName ? ` by ${request.decidedByName}` : ''}
          </p>
          {request?.decisionNote && <p className="mb-1 text-[var(--co-ink-2)]">“{request.decisionNote}”</p>}
          <p className="text-[var(--co-ink-3)]">You can ask again if someone mentions you on this ticket again.</p>
        </div>
      );
    case 'access_removed':
      return (
        <div className={box} data-testid="access-request-removed">
          <p className="text-[var(--co-ink-3)]">
            You were given access earlier, but it has since been removed. You can ask again if someone mentions you on
            this ticket again.
          </p>
        </div>
      );
    case 'not_mentioned':
    default:
      return (
        <p className="mb-7 text-[12.5px] text-[var(--co-ink-4)]" data-testid="access-request-not-mentioned">
          Only teammates @mentioned on a ticket can ask for access to it.
        </p>
      );
  }
}
